import { http } from "../http"

class PriceFetchApi {
    /**
     * Get price adjustments with optional filtering
     * @param {Object} options - Query parameters
     * @param {number} options.page - Page number (default: 1)
     * @param {number} options.limit - Items per page (default: 10)
     * @param {string} options.search - Optional search term
     * @returns {Promise} Promise with price adjustments data
     */
    static async getPriceAdjustments(options = {}) {
        try {
            const params = {
                page: options.page || 1,
                limit: options.limit || 10,
                search: options.search || ''
            };
            
            const response = await http.get('/price-adjustments', { 
                params,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });
            
            return response.data;
        } catch (err) {
            console.error("Error fetching price adjustments:", err);
            throw err;
        }
    }

    /**
     * Get details for a specific price adjustment
     * @param {string} id - Price adjustment ID or name
     * @returns {Promise} Promise with price adjustment details
     */
    static async getPriceAdjustmentDetails(id) {
        try {
            const response = await http.get(`/price-adjustments/${id}`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });
            
            return response.data;
        } catch (err) {
            console.error("Error fetching price adjustment details:", err);
            throw err;
        }
    }

    /**
     * Create a new price adjustment
     * @param {Array} products - Array of products to adjust prices for
     * @returns {Promise} Promise with created price adjustment data
     */
    static async createPriceAdjustment(products) {
        try {
            const response = await http.post('/price-adjustments', { 
                addedProducts: products 
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });
            
            return response.data;
        } catch (err) {
            console.error("Error creating price adjustment:", err);
            throw err;
        }
    }

    /**
     * Get price history for a specific item
     * @param {string} itemCode - Item code
     * @param {string} warehouseId - Optional warehouse ID
     * @returns {Promise} Promise with price history data
     */
    static async getPriceHistory(itemCode, warehouseId = null) {
        try {
            const url = warehouseId 
                ? `/price-adjustments/history/${itemCode}/${warehouseId}`
                : `/price-adjustments/history/${itemCode}`;
            
            const response = await http.get(url, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });
            
            return response.data;
        } catch (err) {
            console.error("Error fetching price history:", err);
            throw err;
        }
    }
    
    /**
     * Get warehouse-specific price for a product
     * @param {string} itemCode - Item code
     * @param {string} warehouseId - Warehouse ID
     * @returns {Promise} Promise with price data
     */
    static async getWarehouseSpecificPrice(itemCode, warehouseId) {
        try {
            const response = await http.get(`/item-prices/${itemCode}`, {
                params: {
                    company_level: warehouseId === null ? 'true' : 'false',
                    warehouse: warehouseId || ''
                },
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });
            
            return response.data;
        } catch (err) {
            console.error("Error fetching warehouse-specific price:", err);
            throw err;
        }
    }
    
    /**
     * Get price adjustment reasons with optional category filtering
     * @param {string} category - Optional category to filter reasons
     * @returns {Promise} Promise with price adjustment reasons
     */
    static async getPriceAdjustmentReasons(category = null) {
        try {
            const params = {};
            if (category) {
                params.category = category;
            }
            
            const response = await http.get('/price-adjustment-reasons', {
                params,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });
            
            return response.data;
        } catch (err) {
            console.error("Error fetching price adjustment reasons:", err);
            throw err;
        }
    }
    
    /**
     * Get user information by user ID
     * @param {string} userId - User ID
     * @returns {Promise} Promise with user data
     */
    static async getUserInfo(userId) {
        try {
            const response = await http.get(`/users/${userId}`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });
            
            return response.data;
        } catch (err) {
            console.error("Error fetching user information:", err);
            throw err;
        }
    }
}

export default PriceFetchApi;
